import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { builder } from '@builder.io/react';
import { getEnvironmentContent } from '../utils/environment';
import { FallbackPage } from '../components/FallbackPage';
import { Meta } from '../components/Meta/Meta';
import { PageBuilderComponent } from '../components/PageBuilderComponent';
import { WistiaControlScript } from '../components/WistiaVideo/WistiaControlScript';
import { UniversalLayout } from '../components/UniversalLayout/UniversalLayout';
import { SurveyContainer } from '../components/Survey/Container';

type Props = {
  content: any;
};

type Query = {
  slug?: string[];
};

export default function BuilderPage({ content }: Props) {
  const router = useRouter();

  // https://nextjs.org/docs/basic-features/data-fetching#fallback-pages
  if (router.isFallback) {
    return <FallbackPage />;
  }

  const {
    description,
    openGraphImageUrl,
    openToSearchEngines,
    showSurvey,
    title,
    universalFooter,
    universalLayout,
    alertBanner,
  } = content?.data || {};

  return (
    <UniversalLayout
      alertBanner={alertBanner}
      hideFooter={universalLayout?.hideFooter}
      hideHeader={universalLayout?.hideHeader}
      shouldRenderAlphabetNav={universalFooter?.shouldRenderAlphabetNav}
      shouldRenderSeoLinks={universalFooter?.shouldRenderSeoLinks}
    >
      <Meta
        description={description}
        openGraphImageUrl={openGraphImageUrl}
        openToSearchEngines={openToSearchEngines}
        title={title}
      />
      <WistiaControlScript />
      <PageBuilderComponent content={content} />
      {showSurvey && <SurveyContainer />}
    </UniversalLayout>
  );
}

export const getStaticProps: GetStaticProps<Props, Query> = async (context) => {
  const pageURL = `/${context.params?.slug?.join('/') || ''}`;

  const content = await builder
    .get('page', {
      query: {
        data: {
          environment: {
            $in: getEnvironmentContent(process.env.NEXT_PUBLIC_ENVIRONMENT),
          },
        },
      },
      url: pageURL,
      cachebust: true,
    })
    .promise();

  if (!content) {
    /**
     * There are various subpaths of `/features` that are floating around so we
     * want to redirect those to the root features page if a page doesn't exist
     * for that path in Builder. This matches the previous behavior when the
     * features page was in the monolith
     */
    if (pageURL.startsWith('/features/')) {
      return {
        redirect: {
          destination: '/features',
          permanent: true,
        },
      };
    }

    return {
      // pageURL is used for client side data-fetching in when previewing/editing
      props: { pageURL },
      notFound: true,
      revalidate: 60,
    };
  }

  return {
    props: { content },
    revalidate: 60, // https://nextjs.org/docs/basic-features/data-fetching#incremental-static-regeneration
  };
};

export const getStaticPaths: GetStaticPaths<Query> = async () => {
  const paths = (
    await builder.getAll('page', {
      cachebust: true,
      fields: 'data.url',
      options: { noTargeting: true },
    })
  ).flatMap((page) => (page.data?.url ? [page.data.url] : []));

  /**
   * Ensures high touch pages are generated on initial build
   * TODO: remove after Builder team fixes `builder.getAll`
   */
  [
    '/',
    '/l/0958ad48c0239b1ada41db723b899d82166d01a4',
    '/how-it-works',
    '/features',
    '/transfer',
    '/security-compliance',
  ].forEach((path) => {
    if (paths.indexOf(path) < 0) {
      paths.push(path);
    }
  });

  return {
    paths,
    fallback: true, // Enable Incremental Static Regeneration https://nextjs.org/docs/basic-features/data-fetching#fallback-true
  };
};
